import React, { useState, useEffect } from 'react';
import { ShoppingBag, Eye, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

// Helper function to fix local 127.0.0.1 and relative image URLs for live server
const getCorrectImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80';

    if (url.includes('127.0.0.1:8000') || url.includes('localhost:8000')) {
        return url.replace(/https?:\/\/(127\.0\.0\.1|localhost):8000/, 'https://rudrakshenterprises.liveindiatech.com');
    }

    if (url.startsWith('/')) {
        return `https://rudrakshenterprises.liveindiatech.com${url}`;
    }

    return url;
};

export default function ProductSlider({ title, subtitle, badge, products, addToCart, onViewDetails }) {
    const sliderId = title ? `slider-container-${title.replace(/\s+/g, '')}` : 'slider-container-default';

    // 1. ALL HOOKS MUST BE CALLED AT THE TOP (Before any early returns)
    useEffect(() => {
        if (!products || products.length === 0) return;

        const container = document.getElementById(sliderId);
        if (!container) return;

        const interval = setInterval(() => {
            if (container) {
                if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
                    container.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    container.scrollBy({ left: 280, behavior: 'smooth' });
                }
            }
        }, 3500);

        return () => clearInterval(interval);
    }, [sliderId, products]);

    // 2. Early return comes AFTER all hooks
    if (!products || products.length === 0) {
        return null;
    }

    // Remove duplicate products based on ID first to prevent double rendering
    const uniqueProducts = Array.from(new Map(products.map(item => [item.id, item])).values());

    const scrollLeft = () => {
        const container = document.getElementById(sliderId);
        if (container) {
            container.scrollBy({ left: -300, behavior: 'smooth' });
        }
    };

    const scrollRight = () => {
        const container = document.getElementById(sliderId);
        if (container) {
            container.scrollBy({ left: 300, behavior: 'smooth' });
        }
    };

    return (
        <section className="w-full px-4 sm:px-8 md:px-12 lg:px-20 py-12 bg-white overflow-hidden border-b border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-10 sm:mb-14">
                <div className="text-center sm:text-left mb-4 sm:mb-0">
                    {badge && (
                        <div className="inline-flex items-center gap-1.5 text-orange-600 text-xs font-bold uppercase tracking-wider mb-2">
                            <Sparkles size={14} /> {badge}
                        </div>
                    )}
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 tracking-tight">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="text-xs sm:text-sm text-gray-500 mt-2">
                            {subtitle}
                        </p>
                    )}
                </div>

                {/* Left / Right Navigation Buttons */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={scrollLeft}
                        className="w-10 h-10 rounded-full bg-orange-50 hover:bg-orange-600 text-orange-600 hover:text-white flex items-center justify-center transition border border-orange-200 cursor-pointer shadow-sm"
                        aria-label="Scroll Left"
                    >
                        <ChevronLeft size={20} />
                    </button>
                    <button
                        onClick={scrollRight}
                        className="w-10 h-10 rounded-full bg-orange-50 hover:bg-orange-600 text-orange-600 hover:text-white flex items-center justify-center transition border border-orange-200 cursor-pointer shadow-sm"
                        aria-label="Scroll Right"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            {/* Scrollable Container */}
            <div
                id={sliderId}
                className="flex gap-6 sm:gap-8 overflow-x-auto scrollbar-hide py-6 px-2 scroll-smooth"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                {uniqueProducts.map((product, index) => {
                    const primaryImage = getCorrectImageUrl(product.image);
                    const galleryImages = product.images && product.images.length > 0
                        ? product.images.map(img => getCorrectImageUrl(img.image))
                        : [];

                    return (
                        <div
                            key={`${product.id}-${index}`}
                            className="relative w-64 sm:w-72 bg-white rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col justify-between pt-16 pb-6 px-6 mt-12 group flex-shrink-0 cursor-pointer"
                        >
                            <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32 bg-white rounded-2xl border-2 border-orange-200 shadow-md overflow-hidden flex items-center justify-center p-1">
                                <img
                                    src={primaryImage}
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=600&q=80'; }}
                                    alt={product.name}
                                    className="w-full h-full object-contain group-hover:scale-110 transition duration-500 rounded-xl"
                                />
                            </div>

                            <span className="absolute top-3 right-3 bg-orange-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-md shadow z-10">
                                {product.discount || 'Off 10%'}
                            </span>

                            <div className="text-center mt-6">
                                <h3 className="font-extrabold text-gray-900 text-sm sm:text-base line-clamp-1">{product.name}</h3>
                                <p className="text-xs text-gray-400 mt-1 line-clamp-1">{product.description || 'Verified Quality Product'}</p>

                                <div className="mt-4 flex items-center justify-center gap-3">
                                    <span className="text-xs text-gray-400 line-through">₹{product.original_price || product.originalPrice}</span>
                                    <span className="text-lg font-black text-orange-600">₹{product.price}</span>
                                </div>

                                {galleryImages.length > 0 && (
                                    <div className="flex items-center justify-center gap-1.5 mt-3">
                                        {galleryImages.map((imgUrl, imgIdx) => (
                                            <div key={imgIdx} className="w-8 h-8 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 p-0.5">
                                                <img src={imgUrl} alt="extra" className="w-full h-full object-cover rounded" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="mt-6 pt-4 border-t border-gray-100 grid grid-cols-2 gap-2 relative z-20">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onViewDetails) onViewDetails(product);
                                    }}
                                    className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 shadow-sm cursor-pointer border-none"
                                >
                                    <Eye size={14} /> Details
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        addToCart(product);
                                    }}
                                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-xl text-xs font-bold transition flex items-center justify-center gap-1 shadow-sm cursor-pointer border-none"
                                >
                                    <ShoppingBag size={14} /> Add
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}